import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Hero from './components/Hero';
import Solutions from './components/Solutions';
import Benefits from './components/Benefits';
import Savings from './components/Savings';
import Process from './components/Process';
import Projects from './components/Projects';
import Financing from './components/Financing';
import Impact from './components/Impact';
import Estimate from './components/Estimate';
import Footer from './components/Footer';
import { readHomeReturn, clearHomeReturn } from '@/utils/homeReturnStore';

const VALID_SECTIONS = new Set(['solutions', 'projects', 'estimate', 'savings', 'process', 'financing']);

export default function Home() {
  const location = useLocation();

  // Deterministic scroll restoration on homepage mount. Runs synchronously
  // before paint (useLayoutEffect) so the user never sees the page flashing
  // at scrollY = 0. If nothing is stored, fall back to the URL hash if any,
  // otherwise leave the page at 0.
  useLayoutEffect(() => {
    const saved = readHomeReturn();
    if (saved) {
      clearHomeReturn();
      // Priority 1: exact scrollY restore when we have a real position.
      // This handles "came from homepage" cases (manual scroll OR top-nav
      // Solutions click) so the user lands right back where they were.
      if (saved.scrollY > 0) {
        window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
        requestAnimationFrame(() => {
          if (Math.abs(window.scrollY - saved.scrollY) > 4) {
            window.scrollTo({ top: saved.scrollY, behavior: 'auto' });
          }
        });
        return;
      }
      // Priority 2: no scrollY (direct-entry fallback OR estimate CTA) —
      // land on the named section.
      if (saved.section && VALID_SECTIONS.has(saved.section)) {
        requestAnimationFrame(() => {
          const el = document.getElementById(saved.section as string);
          if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
      }
      return;
    }

    if (location.hash) {
      const id = location.hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-solar-bg text-solar-ink">
      <main>
        <Hero />
        <Solutions />
        <Benefits />
        <Savings />
        <Process />
        <Projects />
        <Financing />
        <Impact />
        <Estimate />
      </main>
      <Footer />
    </div>
  );
}